import React, { useState, useEffect } from 'react';
import { icons } from './ui/Icons';
import Button from './ui/Button';
import {
  connectGoogleCalendar,
  disconnectGoogleCalendar,
  checkCalendarConnection,
} from '../services/googleCalendarService';
import toast from 'react-hot-toast';

const GoogleCalendarConnect = ({ userId }) => {
  const [loading, setLoading] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState({
    connected: false,
    tokenValid: false,
    connectedAt: null,
  });

  const checkConnection = async () => {
    const status = await checkCalendarConnection(userId);
    setConnectionStatus(status);
  };

  useEffect(() => {
    checkConnection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const handleConnect = async () => {
    setLoading(true);
    try {
      const result = await connectGoogleCalendar(userId);
      if (result.success) {
        toast.success(result.message);
        await checkConnection();
      } else {
        toast.error(result.error);
      }
    } catch (error) {
      toast.error('Erro ao conectar com Google Calendar');
    } finally {
      setLoading(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Tem certeza que deseja desconectar o Google Calendar?')) {
      return;
    }

    setLoading(true);
    const result = await disconnectGoogleCalendar(userId);
    if (result.success) {
      toast.success(result.message);
      await checkConnection();
    } else {
      toast.error(result.error);
    }
    setLoading(false);
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-start justify-between">
        <div className="flex items-center space-x-3">
          <div className="flex-shrink-0">
            <svg className="h-10 w-10" viewBox="0 0 48 48">
              <path fill="#1976d2" d="M24,4C13,4,4,13,4,24s9,20,20,20s20-9,20-20S35,4,24,4z"/>
              <path fill="#fff" d="M35.7,25.3h-3.4v-1.6h3.4c0.4,0,0.8-0.3,0.8-0.8v-3.9c0-0.4-0.3-0.8-0.8-0.8h-7.5v-3.4h7.5c2.1,0,3.8,1.7,3.8,3.8v3.9C39.5,23.6,37.8,25.3,35.7,25.3z"/>
              <path fill="#fff" d="M28.2,34.7h-7.5c-2.1,0-3.8-1.7-3.8-3.8v-3.9c0-2.1,1.7-3.8,3.8-3.8h3.4v3.4h-3.4c-0.4,0-0.8,0.3-0.8,0.8v3.9c0,0.4,0.3,0.8,0.8,0.8h7.5V34.7z"/>
              <rect x="24" y="22" fill="#fff" width="8.2" height="3"/>
            </svg>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              Google Calendar
            </h3>
            <p className="text-sm text-gray-500">
              {connectionStatus.connected && connectionStatus.tokenValid
                ? 'Conectado e sincronizando'
                : connectionStatus.connected && !connectionStatus.tokenValid
                ? 'Token expirado - reconecte'
                : 'Sincronize seus agendamentos automaticamente'}
            </p>
          </div>
        </div>

        <div className="flex-shrink-0">
          {connectionStatus.connected && connectionStatus.tokenValid ? (
            <div className="flex items-center space-x-2">
              <span className="flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-3 w-3 rounded-full bg-green-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
              </span>
              <span className="text-sm font-medium text-green-600">Ativo</span>
            </div>
          ) : (
            <div className="flex items-center space-x-2">
              <span className="inline-flex h-3 w-3 rounded-full bg-gray-300"></span>
              <span className="text-sm font-medium text-gray-500">Inativo</span>
            </div>
          )}
        </div>
      </div>

      <div className="mt-6">
        {connectionStatus.connected && connectionStatus.tokenValid ? (
          <div className="space-y-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  {icons.check({ className: 'h-5 w-5 text-green-600' })}
                </div>
                <div className="ml-3">
                  <h4 className="text-sm font-medium text-green-800">
                    Sincronização Ativa
                  </h4>
                  <div className="mt-2 text-sm text-green-700">
                    <ul className="list-disc list-inside space-y-1">
                      <li>Novos agendamentos são adicionados automaticamente</li>
                      <li>Confirmações e cancelamentos são atualizados</li>
                      <li>Clientes recebem convites por email</li>
                    </ul>
                  </div>
                  {connectionStatus.connectedAt && (
                    <p className="mt-2 text-xs text-green-600">
                      Conectado em: {new Date(connectionStatus.connectedAt.seconds * 1000).toLocaleString('pt-BR')}
                    </p>
                  )}
                </div>
              </div>
            </div>

            <Button
              variant="secondary"
              onClick={handleDisconnect}
              disabled={loading}
              className="w-full"
            >
              {loading ? 'Desconectando...' : 'Desconectar Google Calendar'}
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  {icons.alert({ className: 'h-5 w-5 text-blue-600' })}
                </div>
                <div className="ml-3">
                  <h4 className="text-sm font-medium text-blue-800">
                    {connectionStatus.connected && !connectionStatus.tokenValid
                      ? 'Token Expirado'
                      : 'Benefícios da Sincronização'}
                  </h4>
                  <div className="mt-2 text-sm text-blue-700">
                    <ul className="list-disc list-inside space-y-1">
                      <li>Visualize agendamentos no seu calendário pessoal</li>
                      <li>Receba notificações do Google</li>
                      <li>Acesse de qualquer dispositivo</li>
                      <li>Evite conflitos de horários</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <Button
              variant="primary"
              onClick={handleConnect}
              disabled={loading}
              className="w-full flex items-center justify-center space-x-2"
            >
              <svg className="h-5 w-5" viewBox="0 0 48 48">
                <path fill="#fff" d="M24,4C13,4,4,13,4,24s9,20,20,20s20-9,20-20S35,4,24,4z"/>
              </svg>
              <span>
                {loading
                  ? 'Conectando...'
                  : connectionStatus.connected && !connectionStatus.tokenValid
                  ? 'Reconectar Google Calendar'
                  : 'Conectar Google Calendar'}
              </span>
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default GoogleCalendarConnect;
